import { compactTokenKey } from '@/utils/tokenPatterns'

export interface SplitPiece {
  text: string
  label?: string | null
}

export interface SplitConcept {
  kind: 'class' | 'room'
  example?: string
  pieces: SplitPiece[]
}

export interface ConfirmedToken {
  text: string
  field: string
}

export interface SubjectRef {
  id: string
  labels: string[]
}

export interface SplitHints {
  classConcepts: SplitConcept[]
  roomConcepts: SplitConcept[]
  classes: string[]
  rooms: string[]
  subjects: string[]
  subjectRefs?: SubjectRef[]
  confirmed?: ConfirmedToken[]
  failed?: string[]
}

export const EMPTY_SPLIT_HINTS: SplitHints = {
  classConcepts: [],
  roomConcepts: [],
  classes: [],
  rooms: [],
  subjects: [],
}

const LETTER = '[A-Za-zÄÖÜäöüß]'
const SEP = '[\\s.\\-_/]+'
const SEP_CHAR = /[\s/._-]/
const MAX_TOKEN = 120
const MAX_NODES = 200

interface Atom {
  name: string | null
  source: string
}

interface Compiled {
  atoms: Atom[]
  pattern: RegExp
  specificity: number
  anchor: string
}

type IndexedMatch = RegExpExecArray & {
  indices?: { groups?: Record<string, [number, number] | undefined> }
}

type HitKind = 'confirmed' | 'pattern' | 'matrix'

interface Hit {
  tone: Exclude<PartTone, null>
  kind: HitKind
  anchor: string
}

interface Span extends Hit {
  start: number
  end: number
}

interface Partition {
  spans: Span[]
  cuts: number[]
}

interface Index {
  classRows: Compiled[]
  roomRows: Compiled[]
  exact: Map<string, Set<string>>
  compact: Map<string, Set<string>>
  subjects: Map<string, Set<string>>
}

export function withConfirmed(hints: SplitHints, extra: ConfirmedToken[] | undefined): SplitHints {
  if (!extra?.length) return hints
  return { ...hints, confirmed: [...(hints.confirmed || []), ...extra] }
}

function escapeRe(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const MARKER = '[*∗✱⁎＊✳]'

function isMarkerChar(ch: string): boolean {
  return /[*∗✱⁎＊✳]/.test(ch)
}

function isMarker(text: string): boolean {
  return text.length > 0 && [...text].every((ch) => isMarkerChar(ch))
}

function stripEdgeMarkers(text: string): string {
  return text.replace(/^[*∗✱⁎＊✳]+|[*∗✱⁎＊✳]+$/g, '')
}

function ignoreSource(text: string): string {
  if (isSep(text)) return SEP
  if (isMarker(text)) return `${MARKER}*`
  return escapeRe(text)
}

function isSep(text: string): boolean {
  return /^[\s.\-_/]+$/.test(text)
}

function isSepChar(ch: string): boolean {
  return SEP_CHAR.test(ch)
}

function edgeSep(text: string): boolean {
  return text.length > 0 && (isSepChar(text[0]!) || isSepChar(text[text.length - 1]!))
}

function norm(value: string): string {
  return (value || '').trim().toLowerCase()
}

function unique(values: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const value of values) {
    const text = (value || '').trim()
    const key = text.toLowerCase()
    if (!text || seen.has(key)) continue
    seen.add(key)
    out.push(text)
  }
  return out
}

function compileAtoms(atoms: Atom[], flags: string): Compiled | null {
  const body = atoms
    .map((atom) => (atom.name ? `(?<${atom.name}>${atom.source})` : atom.source))
    .join('')
  try {
    return {
      atoms,
      pattern: new RegExp(`^${body}$`, flags),
      specificity: atoms.filter((atom) => atom.name).length,
      anchor: '',
    }
  } catch {
    return null
  }
}

function trailingSuffixLength(pieces: SplitPiece[]): number {
  let total = 0
  for (let index = pieces.length - 1; index >= 0; index -= 1) {
    const label = pieces[index]?.label || ''
    if (label === 'letter' || label === 'track') total += Math.max(1, (pieces[index]?.text || '').trim().length)
    else if (label === 'ignore') continue
    else break
  }
  return total
}

function labelSequence(pieces: SplitPiece[]): string {
  return pieces
    .map((piece) => piece.label || '')
    .filter((label) => label && label !== 'ignore')
    .join('/')
}

function suffixBoundsByShape(concepts: SplitConcept[]): Map<string, { min: number; max: number }> {
  const groups = new Map<string, number[]>()
  for (const concept of concepts) {
    if (concept.kind !== 'class') continue
    const key = labelSequence(concept.pieces)
    const length = trailingSuffixLength(concept.pieces)
    if (!key || length <= 0) continue
    const list = groups.get(key) || []
    list.push(length)
    groups.set(key, list)
  }
  const bounds = new Map<string, { min: number; max: number }>()
  for (const [key, lengths] of groups) {
    if (lengths.length < 2) continue
    const min = Math.min(...lengths)
    const max = Math.max(...lengths)
    if (max > min) bounds.set(key, { min, max })
  }
  return bounds
}

function classCompiled(pieces: SplitPiece[], bounds: { min: number; max: number } | null): Compiled | null {
  const seen = new Set<string>()
  const atoms: Atom[] = []
  const own = trailingSuffixLength(pieces)
  const extra = bounds && own === bounds.min ? bounds.max - own : 0
  let flexLeft = pieces.filter((piece) => piece.label === 'letter' || piece.label === 'track').length
  for (const piece of pieces) {
    const label = piece.label || ''
    if (label !== 'faculty' && label !== 'year' && label !== 'track' && label !== 'letter' && label !== 'ignore') {
      return null
    }
    if (label !== 'ignore') {
      if (seen.has(label)) return null
      seen.add(label)
    }
    if (label === 'faculty') atoms.push({ name: 'code', source: `${LETTER}{2,}(?!${LETTER})` })
    else if (label === 'year') {
      const digits = piece.text.trim()
      atoms.push({ name: 'year', source: /^\d{4}$/.test(digits) ? '\\d{4}' : '\\d+' })
    }
    else if (label === 'track' || label === 'letter') {
      flexLeft -= 1
      const length = Math.max(1, piece.text.trim().length)
      const span = flexLeft === 0 && extra > 0 ? `${LETTER}{${length},${length + extra}}` : `${LETTER}{${length}}`
      atoms.push({ name: label === 'letter' ? 'letter' : 'middle', source: span })
    } else atoms.push({ name: null, source: ignoreSource(piece.text) })
  }
  if (![...seen].some((label) => label === 'faculty' || label === 'year' || label === 'letter')) return null
  const compiled = compileAtoms(atoms, 'idu')
  if (!compiled) return null
  const faculty = pieces.find((piece) => piece.label === 'faculty')?.text.trim().toLowerCase() ?? ''
  return { ...compiled, anchor: faculty }
}

function numberSource(text: string): string | null {
  if (/^\d+$/.test(text)) return '\\d+'
  const letters = text.match(/^\d+([A-Za-zÄÖÜäöüß]+)$/)
  if (!letters?.[1]) return null
  return `\\d+${LETTER}{${letters[1].length}}`
}

function sepBetween(pieces: SplitPiece[], left: string, right: string): boolean {
  const labels = pieces.map((piece) => piece.label || '')
  const i = labels.indexOf(left)
  const j = labels.indexOf(right)
  if (i < 0 || j < 0) return false
  const lo = Math.min(i, j)
  const hi = Math.max(i, j)
  return pieces.slice(lo + 1, hi).some((piece) => piece.label === 'ignore' && isSep(piece.text))
}

function roomStrict(example: string, pieces: SplitPiece[]): Compiled | null {
  const seen = new Set<string>()
  for (const piece of pieces) {
    const label = piece.label || ''
    if (label !== 'prefix' && label !== 'number' && label !== 'ignore') return null
    if (label !== 'ignore') {
      if (seen.has(label)) return null
      seen.add(label)
    }
  }
  if (seen.size === 0) return null
  if (seen.has('prefix') && !seen.has('number')) {
    const compiled = compileAtoms([{ name: null, source: escapeRe(example.trim()) }], 'iu')
    return compiled ? { ...compiled, anchor: example.trim().toLowerCase() } : null
  }
  const separated = sepBetween(pieces, 'prefix', 'number')
  const atoms: Atom[] = []
  for (const piece of pieces) {
    const label = piece.label || ''
    if (label === 'prefix') {
      const text = piece.text.trim()
      if (!text) return null
      atoms.push({ name: 'prefix', source: escapeRe(text) })
    } else if (label === 'number') {
      const source = separated ? '[A-Za-z0-9]+' : numberSource(piece.text.trim())
      if (!source) return null
      atoms.push({ name: 'number', source })
    } else {
      atoms.push({ name: null, source: ignoreSource(piece.text) })
    }
  }
  const compiled = compileAtoms(atoms, 'iu')
  if (!compiled) return null
  const prefix = pieces.find((piece) => piece.label === 'prefix')?.text.trim().toLowerCase() ?? ''
  return { ...compiled, anchor: prefix }
}

function compiledConcepts(concepts: SplitConcept[], kind: 'class' | 'room'): Compiled[] {
  const boundsByShape = kind === 'class' ? suffixBoundsByShape(concepts) : null
  const out: Compiled[] = []
  for (const concept of concepts) {
    if (concept.kind !== kind) continue
    const example = concept.example || concept.pieces.map((piece) => piece.text).join('')
    const bounds = boundsByShape?.get(labelSequence(concept.pieces)) ?? null
    const compiled = kind === 'room' ? roomStrict(example, concept.pieces) : classCompiled(concept.pieces, bounds)
    if (compiled) out.push(compiled)
  }
  return out
}

function matchOne(compiled: Compiled, token: string): IndexedMatch | null {
  return compiled.pattern.exec(token) as IndexedMatch | null
}

function specificClass(compiled: Compiled[], token: string): Compiled | null {
  const hits = compiled.filter((row) => matchOne(row, token))
  if (hits.length === 0) return null
  if (hits.length === 1) return hits[0]!
  const scored = hits.map((hit) => ({ hit, score: hit.atoms.filter((atom) => atom.name).length }))
  const best = Math.max(...scored.map((row) => row.score))
  const top = scored.filter((row) => row.score === best).map((row) => row.hit)
  if (top.length === 1) return top[0]!
  const tokenKey = token.trim().toLowerCase()
  const aligned = top.filter((hit) => hit.anchor && tokenKey.startsWith(hit.anchor))
  if (aligned.length === 1) return aligned[0]!
  return { ...(aligned[0] ?? top[0]!), anchor: '' }
}

export type PartTone = 'subject' | 'room' | 'klass' | 'ignore' | null

function toneOfField(field: string): Exclude<PartTone, null> | null {
  if (field === 'subject') return 'subject'
  if (field === 'room') return 'room'
  if (field === 'ignore') return 'ignore'
  if (field === 'class_name' || field === 'klass' || field === 'class') return 'klass'
  return null
}

function addField(map: Map<string, Set<string>>, key: string, field: string) {
  if (!key) return
  const set = map.get(key) || new Set<string>()
  set.add(field)
  map.set(key, set)
}

function soleField(map: Map<string, Set<string>>, key: string): string | null {
  const fields = map.get(key)
  if (!fields || fields.size !== 1) return null
  return [...fields][0]!
}

function buildIndex(hints: SplitHints, ignored: string[]): Index {
  const exact = new Map<string, Set<string>>()
  const compact = new Map<string, Set<string>>()
  const push = (text: string, field: string) => {
    if (!toneOfField(field)) return
    const raw = (text || '').trim()
    if (!raw) return
    addField(exact, raw.toLowerCase(), field)
    const folded = compactTokenKey(raw)
    if (folded) addField(compact, folded, field)
  }
  for (const row of hints.confirmed || []) push(row.text, row.field)
  for (const name of ignored) push(name, 'ignore')
  const subjects = new Map<string, Set<string>>()
  if (hints.subjectRefs?.length) {
    for (const ref of hints.subjectRefs) {
      for (const label of ref.labels) {
        const key = norm(label)
        if (!key || !ref.id) continue
        const set = subjects.get(key) || new Set<string>()
        set.add(ref.id)
        subjects.set(key, set)
      }
    }
  } else {
    for (const subject of unique(hints.subjects)) {
      const key = norm(subject)
      if (!key) continue
      subjects.set(key, new Set([key]))
    }
  }
  return {
    classRows: compiledConcepts(hints.classConcepts, 'class'),
    roomRows: compiledConcepts(hints.roomConcepts, 'room'),
    exact,
    compact,
    subjects,
  }
}

function roomHitsFor(rows: Compiled[], token: string): Compiled[] {
  const direct = rows.filter((row) => matchOne(row, token))
  if (direct.length > 0) return direct
  const stripped = stripEdgeMarkers(token)
  if (!stripped || stripped === token) return []
  return rows.filter((row) => matchOne(row, stripped))
}

function specificRoom(rows: Compiled[]): Compiled | null {
  if (rows.length === 0) return null
  if (rows.length === 1) return rows[0]!
  const scored = rows.map((hit) => ({ hit, score: hit.specificity, anchor: hit.anchor.length }))
  scored.sort((left, right) => right.score - left.score || right.anchor - left.anchor)
  return scored[0]!.hit
}

function recognizeIndexed(text: string, index: Index): Hit | null {
  const raw = text || ''
  if (!raw.trim()) return null
  const exactField = soleField(index.exact, raw.trim().toLowerCase())
  if (index.exact.has(raw.trim().toLowerCase()) && !exactField) return null
  if (exactField) {
    const tone = toneOfField(exactField)
    if (tone) return { tone, kind: 'confirmed', anchor: '' }
  }
  if (!edgeSep(raw)) {
    const folded = compactTokenKey(raw)
    if (folded && index.compact.has(folded) && !soleField(index.compact, folded)) return null
    const compactField = folded ? soleField(index.compact, folded) : null
    if (compactField) {
      const tone = toneOfField(compactField)
      if (tone) return { tone, kind: 'confirmed', anchor: '' }
    }
  }
  const classHit = specificClass(index.classRows, raw)
  const roomHits = roomHitsFor(index.roomRows, raw)
  const roomHit = specificRoom(roomHits)
  if (classHit && roomHits.length === 1) return null
  if (classHit) return { tone: 'klass', kind: 'pattern', anchor: classHit.anchor }
  if (roomHit) return { tone: 'room', kind: 'pattern', anchor: roomHit.anchor }
  const ids = index.subjects.get(norm(raw))
  if (ids?.size === 1) return { tone: 'subject', kind: 'matrix', anchor: '' }
  return null
}

function sameRun(left: string, right: string): boolean {
  const letter = /[A-Za-zÄÖÜäöüß]/
  const digit = /\d/
  return (letter.test(left) && letter.test(right)) || (digit.test(left) && digit.test(right))
}

function isLooseSpace(ch: string): boolean {
  return ch === ' ' || ch === '\t' || ch === '\u00a0'
}

function withLooseSpaceCuts(token: string, cuts: number[], spans: Span[]): number[] {
  const extra = [...cuts]
  const covered = (index: number) => spans.some((span) => span.start <= index && index < span.end)
  for (const cut of cuts) {
    let left = cut
    while (left > 0 && isLooseSpace(token[left - 1]!) && !covered(left - 1)) left -= 1
    let right = cut
    while (right < token.length && isLooseSpace(token[right]!) && !covered(right)) right += 1
    if (left < cut) extra.push(left)
    if (right > cut) extra.push(right)
  }
  let cursor = 0
  while (cursor < token.length) {
    if (!isLooseSpace(token[cursor]!) || covered(cursor)) {
      cursor += 1
      continue
    }
    const start = cursor
    while (cursor < token.length && isLooseSpace(token[cursor]!) && !covered(cursor)) cursor += 1
    const end = cursor
    const leftHit = spans.some((span) => span.end === start)
    const rightHit = spans.some((span) => span.start === end)
    if (!leftHit && !rightHit) continue
    if (start > 0) extra.push(start)
    if (end < token.length) extra.push(end)
  }
  return normalizeCuts(token, extra)
}

function normalizeCuts(token: string, cuts: number[]): number[] {
  return [...new Set(cuts)]
    .filter((cut) => cut > 0 && cut < token.length)
    .filter((cut) => !(cut === 1 && sameRun(token[0] || '', token[1] || '')))
    .sort((a, b) => a - b)
}

function skipSep(token: string, pos: number, end: number): number {
  let cursor = pos
  while (cursor < end && isSepChar(token[cursor]!)) cursor += 1
  return cursor
}

function matchesFrom(token: string, index: Index, start: number): Span[] {
  const found: Span[] = []
  if (start < 0 || start >= token.length || isSepChar(token[start]!)) return found
  for (let end = token.length; end > start; end--) {
    if (isSepChar(token[end - 1]!)) continue
    const hit = recognizeIndexed(token.slice(start, end), index)
    if (!hit) continue
    found.push({ start, end, tone: hit.tone, kind: hit.kind, anchor: hit.anchor })
  }
  return found
}

function freeStart(token: string, start: number): boolean {
  if (start <= 0) return true
  return isSepChar(token[start - 1] || '')
}

function dropAttachedShop(token: string, spans: Span[]): Span[] {
  return spans.filter((span) => {
    if (span.kind !== 'confirmed' || freeStart(token, span.start)) return true
    if (span.tone === 'ignore') return false
    return !spans.some(
      (host) =>
        host.kind === 'pattern' &&
        host.tone === 'klass' &&
        host.start < span.start &&
        span.start < host.end,
    )
  })
}

function findSpans(token: string, index: Index): Span[] {
  const spans: Span[] = []
  const seen = new Set<string>()
  const queue = [0]
  for (let cursor = 0; cursor < token.length; cursor++) {
    if (isMarkerChar(token[cursor]!)) queue.push(cursor)
    if (!isSepChar(token[cursor]!)) continue
    const next = skipSep(token, cursor, token.length)
    if (next < token.length) queue.push(next)
  }
  const lower = token.toLowerCase()
  for (const anchor of trainedAnchors(index.roomRows)) {
    let from = 0
    while (from < lower.length) {
      const at = lower.indexOf(anchor, from)
      if (at < 0) break
      queue.push(at)
      from = at + anchor.length
    }
  }
  const queued = new Set(queue)
    while (queue.length > 0) {
    const start = queue.shift()!
    const options = matchesFrom(token, index, start)
    for (const span of options) {
      const key = `${span.start}:${span.end}:${span.kind}`
      if (seen.has(key)) continue
      seen.add(key)
      spans.push(span)
    }
    for (const span of options) {
      if (span.end - span.start < 2) continue
      if (queued.has(span.end) || span.end >= token.length || isSepChar(token[span.end]!)) continue
      queued.add(span.end)
      queue.push(span.end)
    }
  }
  const recovered = dropAttachedShop(token, [...spans, ...recoverNeighbors(token, spans, index)])
  const trimmed = trimStolen(recovered)
  const kept = trimmed.filter((span) => !eatsNext(token, span, trimmed) && !swallowsAligned(token, span, trimmed))
  const anchors = trainedAnchors(index.roomRows)
  return absorbRoomMarkers(
    token,
    outsidePrefix(token, extendUnclaimedTail(token, outsidePrefix(token, kept, anchors), index), anchors),
  )
}

function absorbRoomMarkers(token: string, spans: Span[]): Span[] {
  return spans.map((span) => {
    if (span.tone !== 'room' || span.start === 0) return span
    let start = span.start
    while (start > 0 && isMarkerChar(token[start - 1]!)) start -= 1
    return start === span.start ? span : { ...span, start }
  })
}

function prefixZones(token: string, anchors: string[]): Array<[number, number]> {
  const lower = token.toLowerCase()
  const zones: Array<[number, number]> = []
  for (const anchor of anchors) {
    if (anchor.length < 2) continue
    let from = 0
    while (from < lower.length) {
      const at = lower.indexOf(anchor, from)
      if (at < 0) break
      zones.push([at, at + anchor.length])
      from = at + 1
    }
  }
  return zones
}

function outsidePrefix(token: string, spans: Span[], anchors: string[]): Span[] {
  const zones = prefixZones(token, anchors)
  if (zones.length === 0) return spans
  return spans.filter((span) => {
    return !zones.some(([start, end]) => {
      const startsInside = span.start > start && span.start < end
      const shortHead = span.start === start && span.end < end
      return startsInside || shortHead
    })
  })
}

function trainedAnchors(rows: Compiled[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const row of rows) {
    const anchor = row.anchor.trim().toLowerCase()
    if (anchor.length < 2 || seen.has(anchor)) continue
    seen.add(anchor)
    out.push(anchor)
  }
  return out.sort((left, right) => right.length - left.length)
}

function leadingLetters(text: string): string {
  return text.toLowerCase().match(/^[a-zäöüß]+/)?.[0] ?? ''
}

function roomPrefixLength(token: string, span: Span, anchors: string[]): number {
  if (span.tone !== 'room') return 0
  const head = token.slice(span.start, span.end).toLowerCase()
  const anchor = anchors.find((item) => head.startsWith(item))
  if (anchor) return anchor.length
  return leadingLetters(head).length
}

function extendUnclaimedTail(token: string, spans: Span[], index: Index): Span[] {
  const anchors = trainedAnchors(index.roomRows)
  const out = spans.map((span) => ({ ...span }))
  for (const next of out) {
    const prefixLength = roomPrefixLength(token, next, anchors)
    if (prefixLength < 2) continue
    for (const host of out) {
      if (host.tone !== 'klass' || host.end >= next.start || host.end <= host.start) continue
      const gap = token.slice(host.end, next.start)
      if (!/^[A-Za-zÄÖÜäöüß]*[\s.\-_/]*$/.test(gap)) continue
      const letters = gap.replace(/[\s.\-_/]+$/g, '')
      if (!letters || letters.length >= prefixLength) continue
      const roomHead = token.slice(next.start, next.start + prefixLength).toLowerCase()
      if (anchors.some((anchor) => anchor.startsWith(letters.toLowerCase()) && roomHead.startsWith(anchor.slice(letters.length)))) continue
      if (!sameRun(token[host.end - 1] || '', letters[0] || '')) continue
      const recognized = recognizeIndexed(letters, index)
      if (recognized && letters.length > 1) continue
      const end = host.end + letters.length
      if (end > next.start) continue
      host.end = end
    }
  }
  return out
}

function recoverNeighbors(token: string, spans: Span[], index: Index): Span[] {
  const extra: Span[] = []
  const seen = new Set(spans.map((span) => `${span.start}:${span.end}:${span.tone}`))
  for (const span of spans) {
    if (span.kind !== 'pattern' || span.end >= token.length) continue
    if (!sameRun(token[span.end - 1] || '', token[span.end] || '')) continue
    const floor = Math.max(span.start + 2, span.end - 3)
    for (let at = span.end - 1; at >= floor; at -= 1) {
      if (isSepChar(token[at]!)) break
      const neighbor = matchesFrom(token, index, at).find(
        (row) => row.tone !== span.tone && row.end > span.end && row.end - row.start >= 2,
      )
      if (!neighbor) continue
      const key = `${neighbor.start}:${neighbor.end}:${neighbor.tone}`
      if (!seen.has(key)) {
        seen.add(key)
        extra.push(neighbor)
      }
      break
    }
  }
  return extra
}

function eatsNext(token: string, span: Span, spans: Span[]): boolean {
  return spans.some((shorter) => {
    if (shorter.start !== span.start || shorter.end >= span.end) return false
    const nextAt = skipSep(token, shorter.end, token.length)
    return spans.some((next) => next.start === nextAt && nextAt < span.end && next.end > shorter.end)
  })
}

function swallowsAligned(token: string, span: Span, spans: Span[]): boolean {
  if (span.kind !== 'pattern' || !span.anchor) return false
  if (token.slice(span.start, span.end).toLowerCase().startsWith(span.anchor)) return false
  const shorter = spans.some(
    (other) =>
      other !== span &&
      other.start === span.start &&
      other.tone === span.tone &&
      other.kind === 'pattern' &&
      other.end < span.end &&
      other.anchor.length > 0 &&
      token.slice(other.start, other.end).toLowerCase().startsWith(other.anchor),
  )
  if (!shorter || span.end >= token.length) return false
  return sameRun(token[span.end - 1] || '', token[span.end] || '')
}

function trimStolen(spans: Span[]): Span[] {
  return spans.flatMap((span) => {
    const intruders = spans.filter(
      (other) =>
        other !== span &&
        other.tone !== span.tone &&
        other.start > span.start &&
        other.start < span.end &&
        other.end > span.end,
    )
    if (intruders.length === 0) return [span]
    const end = Math.min(...intruders.map((other) => other.start))
    if (end - span.start < 2) return []
    return [{ ...span, end }]
  })
}

function greedyCover(token: string, from: number, to: number, spans: Span[]): boolean {
  const inner = spans.filter((span) => span.start >= from && span.end <= to && span.end - span.start < to - from)
  if (inner.length < 2) return false
  const used: Span[] = []
  let pos = from
  while (pos < to && used.length <= 4) {
    const cursor = skipSep(token, pos, to)
    if (cursor === to) break
    const next = inner
      .filter((span) => span.start === cursor)
      .sort((left, right) => right.end - left.end)[0]
    if (!next) return false
    used.push(next)
    pos = next.end
  }
  if (pos !== to || used.length < 2) return false
  const host = spans.find((span) => span.start === from && span.end === to && span.kind === 'pattern')
  if (!host) return true
  return used.some((span) => span.tone !== host.tone && span.end - span.start > 1)
}

function overlaps(left: Span, right: Span): boolean {
  return left.start < right.end && right.start < left.end
}

function dropGreedyOverlap(spans: Span[]): Span[] {
  return spans.filter((span) => {
    if (span.kind !== 'pattern') return true
    const others = spans.filter((other) => other !== span && other.tone !== span.tone && overlaps(span, other))
    if (others.length === 0) return true
    return !spans.some(
      (inner) =>
        inner !== span &&
        inner.kind === 'pattern' &&
        inner.tone === span.tone &&
        inner.start >= span.start &&
        inner.end <= span.end &&
        inner.end - inner.start < span.end - span.start &&
        others.every((other) => !overlaps(inner, other)),
    )
  })
}

function claimClassSuffix(token: string, spans: Span[]): Span[] {
  const out: Span[] = []
  for (const span of spans) {
    if (span.tone !== 'klass' || span.end >= token.length || span.end <= span.start) {
      out.push(span)
      continue
    }
    const prev = token[span.end - 1] || ''
    const next = token[span.end] || ''
    if (!/[A-Za-zÄÖÜäöüß]/.test(prev) || !/[A-Za-zÄÖÜäöüß]/.test(next)) {
      out.push(span)
      continue
    }
    let end = span.end
    while (end < token.length && /[A-Za-zÄÖÜäöüß]/.test(token[end]!)) end += 1
    if (end - span.end === 1) out.push({ ...span, end })
  }
  return out
}

function extractableSpan(token: string, span: Span): boolean {
  if (span.end <= span.start) return false
  const left = span.start === 0 || isSepChar(token[span.start - 1]!)
  const right = span.end === token.length || isSepChar(token[span.end] || '')
  if (span.end - span.start === 1) return left && right
  return true
}

function dropClashing(spans: Span[]): Span[] {
  const dropped = new Set<Span>()
  for (let i = 0; i < spans.length; i += 1) {
    const left = spans[i]!
    for (let j = i + 1; j < spans.length; j += 1) {
      const right = spans[j]!
      if (left.start < right.end && right.start < left.end) {
        dropped.add(left)
        dropped.add(right)
      }
    }
  }
  return spans.filter((span) => !dropped.has(span))
}

function pieceTexts(token: string, cuts: number[]): string[] {
  const points = [0, ...cuts, token.length]
  const out: string[] = []
  for (let i = 0; i < points.length - 1; i += 1) {
    const text = token.slice(points[i]!, points[i + 1]!).replace(/^[\s.\-_/]+|[\s.\-_/]+$/g, '')
    if (text && !/^[\s.\-_/]+$/.test(text)) out.push(text)
  }
  return out
}

function keepSpans(token: string, spans: Span[]): Span[] {
  const uncovered = dropGreedyOverlap(spans).filter(
    (span) => span.kind !== 'pattern' || !greedyCover(token, span.start, span.end, spans),
  )
  return uncovered.filter(
    (span) =>
      !uncovered.some(
        (other) =>
          other !== span &&
          other.start <= span.start &&
          other.end >= span.end &&
          other.end - other.start > span.end - span.start,
      ),
  )
}

function cutsInside(cuts: number[], spans: Span[]): boolean {
  return cuts.some((cut) => spans.some((span) => span.start < cut && cut < span.end))
}

function collectPartitions(token: string, spans: Span[]): Partition[] | null {
  const at = new Map<number, Span[]>()
  for (const span of spans) {
    const list = at.get(span.start) || []
    list.push(span)
    at.set(span.start, list)
  }
  const out: Partition[] = []
  let nodes = 0
  let overflow = false
  const walk = (pos: number, chosen: Span[]) => {
    if (overflow) return
    nodes += 1
    if (nodes > MAX_NODES) {
      overflow = true
      return
    }
    const cursor = skipSep(token, pos, token.length)
    if (cursor === token.length) {
      if (chosen.length >= 2 && chosen.length <= 4) {
        const cuts = normalizeCuts(token, chosen.slice(0, -1).map((span) => span.end))
        if (!cutsInside(cuts, spans)) out.push({ spans: chosen, cuts })
      }
      return
    }
    if (chosen.length >= 4) return
    for (const span of at.get(cursor) || []) walk(span.end, [...chosen, span])
  }
  walk(0, [])
  if (overflow) return null
  return out
}

function comparePartitions(left: Partition, right: Partition): number {
  const confirmed = (part: Partition) => part.spans.filter((span) => span.kind === 'confirmed').length
  const pattern = (part: Partition) => part.spans.filter((span) => span.kind === 'pattern').length
  const lc = confirmed(left)
  const rc = confirmed(right)
  if (lc !== rc) return lc - rc
  const lp = pattern(left)
  const rp = pattern(right)
  if (lp !== rp) return lp - rp
  const ll = left.spans.map((span) => span.end - span.start).sort((a, b) => b - a)
  const rl = right.spans.map((span) => span.end - span.start).sort((a, b) => b - a)
  const width = Math.max(ll.length, rl.length)
  for (let i = 0; i < width; i++) {
    const av = ll[i] ?? -1
    const bv = rl[i] ?? -1
    if (av !== bv) return av - bv
  }
  return right.cuts.length - left.cuts.length
}

function extractCuts(token: string, spans: Span[]): number[] {
  const [innerStart, innerEnd] = innerBounds(token)
  const rank = (span: Span) => (span.kind === 'confirmed' ? 3 : span.kind === 'pattern' ? 2 : 1)
  const cutsOf = (rows: Span[]) => {
    const cuts: number[] = []
    for (const span of rows) {
      if (span.start > innerStart) cuts.push(span.start)
      if (span.end < innerEnd) cuts.push(span.end)
    }
    return normalizeCuts(token, cuts)
  }
  let partial = spans.filter((span) => span.start > innerStart || span.end < innerEnd)
  if (partial.length === 0) return []
  const cuts = cutsOf(partial)
  if (!cutsInside(cuts, partial)) return cuts
  partial = [...partial].sort((left, right) => {
    const byRank = rank(right) - rank(left)
    if (byRank !== 0) return byRank
    return right.end - right.start - (left.end - left.start)
  })
  const best = partial[0]
  if (!best) return []
  const rival = partial.find((span) => span !== best && overlaps(span, best))
  if (rival && rank(rival) === rank(best) && rival.end - rival.start === best.end - best.start) return []
  return cutsOf([best])
}

function winningCuts(partitions: Partition[]): number[] {
  const byCuts = new Map<string, Partition>()
  for (const part of partitions) {
    const key = part.cuts.join(',')
    const prev = byCuts.get(key)
    if (!prev || comparePartitions(part, prev) > 0) byCuts.set(key, part)
  }
  let best: Partition | null = null
  let tied = false
  for (const part of byCuts.values()) {
    if (!best) {
      best = part
      continue
    }
    const cmp = comparePartitions(part, best)
    if (cmp > 0) {
      best = part
      tied = false
    } else if (cmp === 0) tied = true
  }
  if (!best || tied) return []
  return best.cuts
}

function recognizedSlice(token: string, start: number, end: number, index: Index): boolean {
  let lo = start
  let hi = end
  while (lo < hi && isSepChar(token[lo]!)) lo += 1
  while (hi > lo && isSepChar(token[hi - 1]!)) hi -= 1
  if (lo >= hi) return false
  return recognizeIndexed(token.slice(lo, hi), index) !== null
}

function repairCuts(token: string, cuts: number[], index: Index): number[] {
  let current = normalizeCuts(token, cuts)
  for (let guard = 0; guard < token.length; guard += 1) {
    let moved = false
    const sorted = [...current]
    for (let i = 0; i < sorted.length; i += 1) {
      const cut = sorted[i]!
      const left = i === 0 ? 0 : sorted[i - 1]!
      const right = i + 1 < sorted.length ? sorted[i + 1]! : token.length
      const rightRec = recognizedSlice(token, cut, right, index)
      if (
        cut - 1 > left &&
        recognizedSlice(token, left, cut - 1, index) &&
        recognizedSlice(token, cut - 1, right, index) &&
        !rightRec
      ) {
        sorted[i] = cut - 1
        moved = true
        continue
      }
      if (
        cut + 1 < right &&
        recognizedSlice(token, left, cut, index) &&
        recognizedSlice(token, left, cut + 1, index) &&
        recognizedSlice(token, cut + 1, right, index) &&
        !rightRec
      ) {
        sorted[i] = cut + 1
        moved = true
      }
    }
    current = normalizeCuts(token, sorted)
    if (!moved) break
  }
  return current
}

function innerBounds(token: string): [number, number] {
  let start = 0
  let end = token.length
  while (start < end && isSepChar(token[start]!)) start += 1
  while (end > start && isSepChar(token[end - 1]!)) end -= 1
  return [start, end]
}

export function characterTones(
  token: string,
  cuts: number[],
  hints: SplitHints = EMPTY_SPLIT_HINTS,
  ignored: string[] = [],
): PartTone[] {
  const tones: PartTone[] = Array.from({ length: token.length }, () => null)
  if (!token || token.length > MAX_TOKEN) return tones
  const index = buildIndex(hints, ignored)
  const paint = (start: number, end: number) => {
    let lo = start
    let hi = end
    while (lo < hi && isSepChar(token[lo]!)) lo += 1
    while (hi > lo && isSepChar(token[hi - 1]!)) hi -= 1
    if (lo >= hi) return
    const hit = recognizeIndexed(token.slice(lo, hi), index)
    if (!hit) return
    for (let cursor = lo; cursor < hi; cursor++) tones[cursor] = hit.tone
  }
  const paintSpans = (from: number, to: number) => {
    const slice = token.slice(from, to)
    const spans = keepSpans(slice, findSpans(slice, index))
    for (const span of spans) {
      if (span.start === 0 && span.end === slice.length && !recognizeIndexed(slice, index)) continue
      for (let cursor = from + span.start; cursor < from + span.end && cursor < to; cursor++) {
        tones[cursor] = span.tone
      }
    }
  }
  const sorted = normalizeCuts(token, cuts)
  if (sorted.length === 0) {
    paint(0, token.length)
    paintSpans(0, token.length)
    return tones
  }
  const bounds = [0, ...sorted, token.length]
  for (let index = 0; index < bounds.length - 1; index++) {
    const start = bounds[index]!
    const end = bounds[index + 1]!
    paint(start, end)
    if (!tones.slice(start, end).some((tone) => tone)) paintSpans(start, end)
  }
  return tones
}

export function suggestNestedCuts(part: string, hints: SplitHints = EMPTY_SPLIT_HINTS): number[] {
  return suggestCuts(part, hints)
}

export function suggestCuts(token: string, hints: SplitHints = EMPTY_SPLIT_HINTS): number[] {
  const value = token || ''
  if (value.length < 2 || value.length > MAX_TOKEN) return []
  const index = buildIndex(hints, [])
  const rawSpans = findSpans(value, index)
  const zones = prefixZones(value, trainedAnchors(index.roomRows))
  const finish = (cuts: number[]) =>
    withLooseSpaceCuts(
      value,
      cuts.filter(
        (cut) =>
          !rawSpans.some((span) => span.start < cut && cut < span.end) &&
          !zones.some(([start, end]) => start < cut && cut < end),
      ),
      rawSpans,
    )
  const [innerStart, innerEnd] = innerBounds(value)
  const inner = value.slice(innerStart, innerEnd)
  const whole = inner ? recognizeIndexed(inner, index) : null
  if (whole && (whole.kind === 'confirmed' || whole.kind === 'matrix') && innerStart === 0 && innerEnd === value.length) {
    return finish([])
  }
  if (whole && (whole.kind === 'confirmed' || whole.kind === 'matrix') && inner === value.trim()) return finish([])
  if (whole?.kind === 'pattern') {
    const splitWorth = rawSpans.some(
      (span) =>
        span.tone !== whole.tone &&
        span.end - span.start > 1 &&
        span.start === innerStart &&
        rawSpans.some((next) => next.start === span.end && next.end >= innerEnd && next.tone !== span.tone),
    )
    if (!splitWorth) return finish([])
  }
  const spans = keepSpans(value, rawSpans)
  const covered = spans.some((span) => span.start === innerStart && span.end === innerEnd)
  if (covered && whole && (whole.kind === 'confirmed' || whole.kind === 'matrix')) return finish([])
  const partitions = collectPartitions(value, spans)
  const coveredCuts = partitions && partitions.length > 0 ? winningCuts(partitions) : []
  const raw = coveredCuts.length > 0 ? coveredCuts : extractCuts(value, spans)
  return finish(repairCuts(value, raw, index))
}

function uniqueSpans(spans: Span[]): Span[] {
  const seen = new Set<string>()
  const out: Span[] = []
  for (const span of spans) {
    const key = `${span.start}:${span.end}:${span.tone}:${span.kind}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(span)
  }
  return out
}

export function knownPartCuts(token: string, hints: SplitHints = EMPTY_SPLIT_HINTS): number[] | null {
  const value = token || ''
  if (value.length < 2 || value.length > MAX_TOKEN) return null
  const index = buildIndex(hints, [])
  const [innerStart, innerEnd] = innerBounds(value)
  const inner = value.slice(innerStart, innerEnd)
  if (!inner) return null
  if (recognizeIndexed(inner, index) && suggestCuts(value, hints).length === 0) return null
  const found = uniqueSpans(claimClassSuffix(value, keepSpans(value, findSpans(value, index)))).filter((span) =>
    extractableSpan(value, span),
  )
  const clear = dropClashing(found)
  const partial = clear.filter((span) => span.start > innerStart || span.end < innerEnd)
  if (partial.length === 0) return null
  const cuts = withLooseSpaceCuts(
    value,
    normalizeCuts(value, partial.flatMap((span) => [span.start, span.end])).filter(
      (cut) => !clear.some((span) => span.start < cut && cut < span.end),
    ),
    found,
  ).filter((cut) => !clear.some((span) => span.start < cut && cut < span.end))
  if (cuts.length === 0) return null
  const pieces = pieceTexts(value, cuts).map((part) => part.toLowerCase())
  if (pieces.length < 2) return null
  const isolated = partial.every((span) => {
    const text = value.slice(span.start, span.end).replace(/^[\s.\-_/]+|[\s.\-_/]+$/g, '').toLowerCase()
    return text.length > 0 && pieces.includes(text)
  })
  if (!isolated) return null
  const known = pieces.some((part) => {
    if (recognizeIndexed(part, index)) return true
    if (part.length < 3) return false
    const last = part[part.length - 1] || ''
    const prev = part[part.length - 2] || ''
    if (!/[A-Za-zÄÖÜäöüß]/.test(last) || !/[A-Za-zÄÖÜäöüß]/.test(prev)) return false
    return recognizeIndexed(part.slice(0, -1), index)?.tone === 'klass'
  })
  if (!known) return null
  return cuts
}

export function unambiguousCuts(token: string, hints: SplitHints = EMPTY_SPLIT_HINTS): number[] | null {
  const value = token || ''
  if (value.length < 2 || value.length > MAX_TOKEN) return null
  const index = buildIndex(hints, [])
  const [innerStart, innerEnd] = innerBounds(value)
  const inner = value.slice(innerStart, innerEnd)
  if (inner && recognizeIndexed(inner, index) && suggestCuts(value, hints).length === 0) return null
  const spans = keepSpans(value, findSpans(value, index))
  const partitions = collectPartitions(value, spans)
  if (!partitions || partitions.length === 0) return null
  const won = winningCuts(partitions)
  if (won.length === 0) return null
  const cuts = repairCuts(value, won, index)
  if (cuts.length === 0) return null
  const points = [0, ...cuts, value.length]
  for (let i = 0; i < points.length - 1; i += 1) {
    if (!recognizedSlice(value, points[i]!, points[i + 1]!, index)) return null
  }
  return cuts
}

export function keptWhole(token: string, hints: SplitHints = EMPTY_SPLIT_HINTS): boolean {
  const value = token || ''
  if (!value.trim() || value.length > MAX_TOKEN) return false
  const index = buildIndex(hints, [])
  const [start, end] = innerBounds(value)
  const inner = value.slice(start, end)
  if (!inner || !recognizeIndexed(inner, index)) return false
  return suggestCuts(value, hints).length === 0
}
